import { useState } from "react";
import type { EventVenuePublic } from "@dugads/shared";
import { MapPin, Navigation, Home } from "lucide-react";
import { directionsUrl } from "@/lib/directions";

// 194 -- "where + who" for an event: the hosting (primary) venue with a
// directions link and the participating schools (host marked). Shared by the
// event-detail header (desktop + 390px trees) and the public tournament view
// so the two never drift. Reflows on narrow screens; a long participant list
// is capped with an in-place "+N more" toggle instead of a bottom sheet, which
// keeps the header to ~2 lines without pulling in the sheet machinery.
//
// Takes the structural minimum (not full EventPublic) so the lighter tournament
// summary payload satisfies it too.
interface WhereWhoEvent {
  venues: EventVenuePublic[];
  schools: { school_id: string; name: string; is_host: boolean }[];
}

export function EventWhereWho({ event, className }: { event: WhereWhoEvent; className?: string }) {
  const [showAll, setShowAll] = useState(false);
  const primaryVenue = event.venues.find((v) => v.is_primary) ?? event.venues[0] ?? null;
  const schools = [...event.schools].sort((a, b) =>
    a.is_host === b.is_host ? 0 : a.is_host ? -1 : 1,
  );
  const CAP = 3;
  const visible = showAll ? schools : schools.slice(0, CAP);
  const overflow = schools.length - visible.length;

  if (!primaryVenue && schools.length === 0) return null;

  return (
    <div className={`flex flex-col gap-1.5 text-sm ${className ?? ""}`} data-region="where-who">
      {primaryVenue && (
        <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground" data-region="header-venue">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          <span className="font-medium text-foreground">{primaryVenue.display_name}</span>
          <a
            href={directionsUrl(primaryVenue)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-0.5 text-xs text-primary hover:underline"
            data-action="get-directions"
          >
            <Navigation className="h-3 w-3" /> Directions
          </a>
        </div>
      )}
      {schools.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-region="header-participants">
          {visible.map((s) => (
            <span
              key={s.school_id}
              data-item="participant-school"
              data-item-id={s.school_id}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-xs"
            >
              {s.is_host && <Home className="h-3 w-3 text-primary" aria-label="Host" />}
              <span className="max-w-[10rem] truncate">{s.name}</span>
            </span>
          ))}
          {overflow > 0 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="rounded-full border border-dashed border-border px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
              data-action="show-all-schools"
            >
              +{overflow} more
            </button>
          )}
        </div>
      )}
    </div>
  );
}
